// Client-side email matching engine, extracted from EmailProcessor.vue
// (no behavior change). Pure functions + a pattern builder; matching results
// are content-free categorization metadata (ids only), never email content.

function generateDocketRegexPatterns(docketNumbers) {
  if (!Array.isArray(docketNumbers))  {
    throw new Error("Invalid input: docketNumbers should be an array.");
  }
  if (docketNumbers.length === 0) {
    return [];
  }
  const escapeRegexChar = (ch) => {
    const special = /[.*+?^${}()|[\]\\]/g;
    return ch.replace(special, '\\$&');
  };

  const toCharType = (ch) => {
    if (/[A-Z]/.test(ch)) return 'A'; // uppercase letter
    if (/[a-z]/.test(ch)) return 'a'; // lowercase letter
    if (/\d/.test(ch)) return '9';    // digit
    return escapeRegexChar(ch);       // special character
  };

  const patterns = [];

  for (const str of docketNumbers) {
    let typePattern = '';
    let i = 0;
    while (i < str.length) {
      const type = toCharType(str[i]);
      let count = 1;
      while (i + count < str.length && toCharType(str[i + count]) === type) {
        count++;
      }
      if (type === 'A') typePattern += `[A-Z]{${count}}`;
      else if (type === 'a') typePattern += `[a-z]{${count}}`;
      else if (type === '9') typePattern += `\\d{${count}}`;
      else typePattern += `${type.repeat(count)}`;
      i += count;
    }
    // if the pattern is not already in the patterns array, add it
    if (!patterns.includes(typePattern)) {
      patterns.push(typePattern);
    }
  }
  return patterns;
}

// Builds the full set of matching patterns from the patent/trademark stores,
// reusing cached patterns from emailSettings when the underlying id lists
// haven't changed. Persists refreshed settings via `updateSettings`.
// Returns the patterns object consumed by processMessage.
async function prepMatchingPatterns({ patentStore, trademarkStore, emailSettings, updateSettings }) {

  // 1. prepare matching patterns related to application ids, patent numbers, and docket numbers
  while (patentStore.loaded != true) {
    await new Promise(r => setTimeout(r, 1000));
  }

  const applicationIds = patentStore.all_patents.map(patent => patent.applId);
  // sort the applicationIds in ascending order
  applicationIds.sort((a, b) => a - b);

  let patterns = {
    applicationIds,
    patentNumbers: [],
    patentMap: {},
    docketNumbers: [],
    docketRegexPatterns: [],
    docketRegexes: [],
    docketMap: {},
    tmIds: [],
    tmNumbers: [],
    tmMap: {},
    tmDocketNumbers: [],
    tmDocketRegexPatterns: [],
    tmDocketRegexes: [],
    tmDocketMap: {},
  };

  let isSame = true;
  if (emailSettings && emailSettings.applicationIds) {
    // compare whether applicationIds is the same as emailSettings.applicationIds
    // if they are the same, then reuse the existing emailSettings' patentNumbers,
    // patentMap, docketRegexes, and docketMap
    if (applicationIds.length == emailSettings.applicationIds.length) {
      for (let i = 0; i < applicationIds.length; i++) {
        if (applicationIds[i] != emailSettings.applicationIds[i]) {
          isSame = false;
          break;
        }
      }
      if (isSame) {
        patterns.patentNumbers = emailSettings.patentNumbers;
        patterns.patentMap = emailSettings.patentMap;
        patterns.docketNumbers = emailSettings.docketNumbers;
        patterns.docketRegexPatterns = emailSettings.docketRegexPatterns;
        patterns.docketRegexes = patterns.docketRegexPatterns.map(pattern => new RegExp(`${pattern}`, 'g'));
        patterns.docketMap = emailSettings.docketMap;
      }
    }
  } else {
    isSame = false;
  }
  // else, update the emailSettings with the new applicationIds, new patentNumbers, new docketRegexes, and new docketMap
  if (!isSame) {
    patterns.patentNumbers = patentStore.all_patents.filter(patent => patent.patentNumber).map(patent => patent.patentNumber);
    patterns.patentMap = {};
    for (const patent of patentStore.all_patents) {
      if (patent.patentNumber) {
        patterns.patentMap[patent.patentNumber] = patent.applId;
      }
    }
    // sort the patentNumbers in ascending order
    patterns.patentNumbers.sort((a, b) => a - b);

    patterns.docketNumbers = patentStore.all_patents.filter(patent => patent.appAttrDockNumber).map(patent => patent.appAttrDockNumber);
    patterns.docketMap = {};
    for (const patent of patentStore.all_patents) {
      if (patent.appAttrDockNumber) {
        patterns.docketMap[patent.appAttrDockNumber] = patent.applId;
      }
    }
    patterns.docketRegexPatterns = generateDocketRegexPatterns(patterns.docketNumbers);
    patterns.docketRegexes = patterns.docketRegexPatterns.map(pattern => new RegExp(`${pattern}`, 'g'));
  }

  // 2. prepare matching patterns related to trademark ids, trademark numbers, and docket numbers
  while (trademarkStore.tm_loaded != true) {
    await new Promise(r => setTimeout(r, 1000));
  }

  const tmIds = trademarkStore.the_trademarks.filter(t => !t.foreign).map(trademark => trademark.serialNumber);
  // sort the tmIds in ascending order
  tmIds.sort((a, b) => a - b);
  patterns.tmIds = tmIds;

  let isTMSame = true;
  if (emailSettings && emailSettings.tmIds) {
    if (tmIds.length == emailSettings.tmIds.length) {
      for (let i = 0; i < tmIds.length; i++) {
        if (tmIds[i] != emailSettings.tmIds[i]) {
          isTMSame = false;
          break;
        }
      }
      if (isTMSame) {
        patterns.tmNumbers = emailSettings.tmNumbers;
        patterns.tmMap = emailSettings.tmMap;
        patterns.tmDocketNumbers = emailSettings.tmDocketNumbers;
        patterns.tmDocketRegexPatterns = emailSettings.tmDocketRegexPatterns;
        patterns.tmDocketRegexes = patterns.tmDocketRegexPatterns.map(pattern => new RegExp(`${pattern}`, 'g'));
        patterns.tmDocketMap = emailSettings.tmDocketMap;
      }
    }
  } else {
    isTMSame = false;
  }
  if (!isTMSame) {
    patterns.tmNumbers = trademarkStore.the_trademarks.filter(tm => !tm.foreign && tm.registrationNumber).map(tm => tm.registrationNumber);
    patterns.tmMap = {};
    for (const tm of trademarkStore.the_trademarks) {
      if (tm.foreign) continue;
      if (tm.registrationNumber) {
        patterns.tmMap[tm.registrationNumber] = tm.serialNumber;
      }
    }
    patterns.tmNumbers.sort((a, b) => a - b);

    patterns.tmDocketNumbers = trademarkStore.the_trademarks.filter(tm => !tm.foreign && tm.statusObj?.status?.attrnyDktNumber).map(tm => tm.statusObj.status.attrnyDktNumber);
    patterns.tmDocketMap = {};
    for (const tm of trademarkStore.the_trademarks) {
      if (tm.foreign) continue;
      if (tm.statusObj?.status?.attrnyDktNumber) {
        patterns.tmDocketMap[tm.statusObj.status.attrnyDktNumber] = tm.serialNumber;
      }
    }
    patterns.tmDocketRegexPatterns = generateDocketRegexPatterns(patterns.tmDocketNumbers);
    patterns.tmDocketRegexes = patterns.tmDocketRegexPatterns.map(pattern => new RegExp(`${pattern}`, 'g'));
  }

  await updateSettings(
    {
      applicationIds: patterns.applicationIds,
      patentNumbers: patterns.patentNumbers,
      patentMap: patterns.patentMap,
      docketNumbers: patterns.docketNumbers,
      docketRegexPatterns: patterns.docketRegexPatterns,
      docketMap: patterns.docketMap,
      tmIds: patterns.tmIds,
      tmNumbers: patterns.tmNumbers,
      tmMap: patterns.tmMap,
      tmDocketNumbers: patterns.tmDocketNumbers,
      tmDocketRegexPatterns: patterns.tmDocketRegexPatterns,
      tmDocketMap: patterns.tmDocketMap,
    }
  );

  return patterns;
}

function removeHtml(html) {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || "";
}

// Matches arbitrary text (body, filenames, extracted attachment text) against
// the prepared patterns. Returns id lists + flags.
function matchContent(text, patterns) {
  let patentIds = [];
  let trademarkIds = [];
  let hasTrademark = false;
  let hasPatent = false;

  //TODO this needs to just be the text, not including any tags...
  const numberExp = /[0-9/,]{7,12}/g;

  const idCandidates = text.match(numberExp);

  if (idCandidates) {
    const punctRegex = /,|\//g

    for (const patentId of idCandidates) {
      const formattedId = patentId.replaceAll(punctRegex, "");
      if (patterns.applicationIds.includes(formattedId)) {
        hasPatent = true;
        if (!patentIds.includes(formattedId))
          patentIds.push(formattedId);
      } else if (patterns.patentNumbers.includes(formattedId)) {
        hasPatent = true;
        if (!patentIds.includes(patterns.patentMap[formattedId]))
          patentIds.push(patterns.patentMap[formattedId]);
      }
      else if (patterns.tmIds.includes(formattedId)) {
        hasTrademark = true;
        if (!trademarkIds.includes(formattedId))
          trademarkIds.push(formattedId);
      } else if (patterns.tmNumbers.includes(formattedId)) {
        hasTrademark = true;
        if (!trademarkIds.includes(patterns.tmMap[formattedId]))
          trademarkIds.push(patterns.tmMap[formattedId]);
      }
    }
  }
  for (const docketRegex of patterns.docketRegexes) {
    const docketNumberCandidates = text.match(docketRegex);
    if (docketNumberCandidates) {
      for (const docketNumber of docketNumberCandidates) {
        if (patterns.docketNumbers.includes(docketNumber)) { // note, use docketmap to check instead.
          hasPatent = true;
          if (!patentIds.includes(patterns.docketMap[docketNumber])) {
            patentIds.push(patterns.docketMap[docketNumber]);
          }
        }
      }
    }
  }

  for (const tmDocketRegex of patterns.tmDocketRegexes) {
    const docketNumberCandidates = text.match(tmDocketRegex);
    if (docketNumberCandidates) {
      for (const docketNumber of docketNumberCandidates) {
        if (patterns.tmDocketNumbers.includes(docketNumber)) { // note, use docketmap to check instead.
          hasTrademark = true;
          if (!trademarkIds.includes(patterns.tmDocketMap[docketNumber])) {
            trademarkIds.push(patterns.tmDocketMap[docketNumber]);
          }
        }
      }
    }
  }

  return { patentIds, trademarkIds, hasPatent, hasTrademark };
}

function mergeMatches(target, source) {
  for (const id of source.patentIds) {
    if (!target.patentIds.includes(id)) {
      target.patentIds.push(id);
    }
  }
  for (const id of source.trademarkIds) {
    if (!target.trademarkIds.includes(id)) {
      target.trademarkIds.push(id);
    }
  }
  target.hasPatent = target.hasPatent || source.hasPatent;
  target.hasTrademark = target.hasTrademark || source.hasTrademark;
}

// Matches one raw provider message against the prepared patterns.
// Returns content-free categorization metadata, or null if the message has
// no usable content. `emailApi` is the provider interface (gmail/outlook).
//
// Stage 0 escalation (cost-aware): body+subject first; if nothing matched and
// the message has attachments, try attachment FILENAMES (metadata is cheap);
// only if still unmatched, use attachmentTextProvider to fetch/parse
// attachment content (expensive, cached by the caller).
async function processMessage(message, patterns, emailApi, attachmentTextProvider = null) {
  // note that message now is a detailed message with body, headers, etc.
  const email = message;
  if (!email || !email.id) {
    return null;
  }

  const body = emailApi.getBody(email);
  const subject = emailApi.getSubjectLine(email);
  const textBody = removeHtml(body);

  const email_content = textBody + ' ' + subject;

  if (!email_content) {
    return null;
  }

  const match = matchContent(email_content, patterns);
  const matchedAttachments = [];

  // stage 0: attachments, only when body/subject found nothing
  if (!match.hasPatent && !match.hasTrademark && emailApi.listAttachments) {
    const atts = await emailApi.listAttachments(message);
    if (atts && atts.length > 0) {
      const nameMatch = matchContent(atts.map((a) => a.name).join(' '), patterns);
      if (nameMatch.hasPatent || nameMatch.hasTrademark) {
        mergeMatches(match, nameMatch);
        matchedAttachments.push(...atts.map((a) => a.name));
      } else if (attachmentTextProvider) {
        const attachmentText = await attachmentTextProvider(message, atts);
        if (attachmentText) {
          const contentMatch = matchContent(attachmentText, patterns);
          if (contentMatch.hasPatent || contentMatch.hasTrademark) {
            mergeMatches(match, contentMatch);
            matchedAttachments.push(...atts.map((a) => a.name));
          }
        }
      }
    }
  }

  const platformDate = emailApi.platformDate(email);

  //TODO add unread flag to the record (this is not in the default data...)
  //TODO for threads we need to store the last thread date not the email date...
  //TODO we need to merge the patent ids and client ids incase we aer updating a thread when a new message is sent.
  const result = {
    processed: new Date().toISOString(),
    date: platformDate,
    patentIds: match.patentIds,
    hasPatent: match.hasPatent,
    trademarkIds: match.trademarkIds,
    hasTrademark: match.hasTrademark,
    isRelevant: match.hasPatent || match.hasTrademark,
  };
  // auditability: which attachments produced the match (filenames only,
  // never content)
  if (matchedAttachments.length > 0) {
    result.matchedAttachments = matchedAttachments;
  }
  return result;
}

export {
  generateDocketRegexPatterns,
  prepMatchingPatterns,
  processMessage,
  removeHtml,
};
